import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Json } from '@/integrations/supabase/types';



interface ComposerSessionState {
  sessionId: string | null;
  draftId: string | null;
  policyId: string | null;
  sessionName: string;
  initialHtml: string;
  initialJson: Json | null;
  originalFileUrl: string | null;
  isLoading: boolean;
  isPending: boolean;
  error: string | null;
}

export function useComposerSession(
  urlPolicyId?: string | null,
  isNew?: boolean,
  mode?: string | null,
  idempotencyKey?: string | null,
) {
  const { profile } = useAuth();
  const lastInitializedPolicyId = useRef<string | null>(null);
  const [state, setState] = useState<ComposerSessionState>({
    sessionId: null,
    draftId: null,
    policyId: null,
    sessionName: 'Untitled Policy',
    initialHtml: '',
    initialJson: null,
    originalFileUrl: null,
    isLoading: true,
    isPending: false,
    error: null,
  });

  // stateRef: deliberate exception pattern — used ONLY inside initializeSession
  // so it can read the latest sessionName (typed before first save) without
  // stale-closure bugs. Do NOT replicate this pattern elsewhere in the codebase;
  // prefer state/props for reactive reads.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const initializingRef = useRef<Promise<{
    sessionId: string;
    draftId: string;
    policyId: string;
  } | null> | null>(null);

  useEffect(() => {
    if (!profile) return;

    const currentKey = isNew ? `force-new-${Date.now()}` : (urlPolicyId || 'new');
    if (!isNew && lastInitializedPolicyId.current === currentKey) return;
    lastInitializedPolicyId.current = currentKey;

    // --- Branch 0: ?new=<uuid> — defer creation to first meaningful user action ---
    // Mount-time binding would violate the Section 10 invariant and break
    // under React 18 StrictMode remounts. Row creation happens in
    // initializeSession(), triggered from saveDraft() on first save.
    if (isNew) {
      setState({
        sessionId: null,
        draftId: null,
        policyId: null,
        sessionName: 'Untitled Policy',
        initialHtml: '',
        initialJson: null,
        originalFileUrl: null,
        isLoading: false,
        isPending: true,
        error: null,
      });
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    async function init() {
      try {
        let sessionId: string | null = null;
        let draftId: string | null = null;
        let policyId: string | null = null;
        let sessionName = 'Untitled Policy';
        let contentHtml = '';
        let contentJson: Json | null = null;
        let originalFileUrl: string | null = null;

        if (urlPolicyId) {
          // --- Branch 1: URL policy_id present — strict scoping ---
          const { data: existingSession } = await supabase
            .from('composer_sessions')
            .select('id, session_name, policy_id')
            .eq('policy_id', urlPolicyId)
            .eq('clinic_id', profile!.clinic_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existingSession) {
            const { data: draft } = await supabase
              .from('policy_drafts')
              .select('id, content_html, content_json')
              .eq('session_id', existingSession.id)
              .eq('status', 'draft')
              .limit(1)
              .maybeSingle();

            if (draft) {
              sessionId = existingSession.id;
              draftId = draft.id;
              policyId = existingSession.policy_id;
              sessionName = existingSession.session_name || 'Untitled Policy';
              contentHtml = draft.content_html || '';
              contentJson = draft.content_json;

              // Fetch file_url from latest version (guarded)
              if (policyId) {
                const { data: latestVersion } = await supabase
                  .from('policy_versions')
                  .select('file_url')
                  .eq('policy_id', policyId)
                  .order('version_number', { ascending: false })
                  .limit(1)
                  .maybeSingle();
                originalFileUrl = latestVersion?.file_url ?? null;
              }
            }
          }

          // No session/draft found for this policy — create one
          if (!sessionId) {
            policyId = urlPolicyId;

            let seedHtml = '';
            let seedJson: Json | null = null;
            let genMode = 'new_draft';

            // When mode=update, try to clone content from the most recent submitted draft
            if (mode === 'update') {
              const { data: submittedDraft } = await supabase
                .from('policy_drafts')
                .select('content_html, content_json')
                .eq('policy_id', urlPolicyId)
                .eq('status', 'submitted')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

              if (submittedDraft?.content_html) {
                seedHtml = submittedDraft.content_html;
                seedJson = submittedDraft.content_json;
                genMode = 'revision';
              }
            }

            // Fallback: recover content from any orphaned draft (only if we didn't seed from submitted)
            if (!seedHtml) {
              const { data: orphanedDraft } = await supabase
                .from('policy_drafts')
                .select('content_html, content_json')
                .eq('policy_id', urlPolicyId)
                .eq('status', 'draft')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

              if (orphanedDraft?.content_html) {
                seedHtml = orphanedDraft.content_html;
                seedJson = orphanedDraft.content_json;
              }
            }

            // Fetch policy name and latest version file_url
            const { data: existingPolicy } = await supabase
              .from('policies')
              .select('name')
              .eq('id', policyId)
              .maybeSingle();
            if (existingPolicy?.name) sessionName = existingPolicy.name;

            // policyId is always set here (from urlPolicyId)
            const { data: latestVersion } = await supabase
              .from('policy_versions')
              .select('file_url')
              .eq('policy_id', policyId!)
              .order('version_number', { ascending: false })
              .limit(1)
              .maybeSingle();
            originalFileUrl = latestVersion?.file_url ?? null;

            // Create session
            const { data: newSession, error: nsErr } = await supabase
              .from('composer_sessions')
              .insert({
                clinic_id: profile!.clinic_id,
                created_by: profile!.id,
                session_name: sessionName,
                policy_id: policyId,
              })
              .select('id')
              .single();
            if (nsErr) throw nsErr;
            sessionId = newSession.id;

            // Create draft with seeded or empty content
            const { data: newDraft, error: ndErr } = await supabase
              .from('policy_drafts')
              .insert({
                clinic_id: profile!.clinic_id,
                session_id: sessionId,
                created_by: profile!.id,
                content_html: seedHtml,
                content_json: seedJson,
                status: 'draft',
                generation_mode: genMode,
                policy_id: policyId,
              })
              .select('id')
              .single();
            if (ndErr) throw ndErr;
            draftId = newDraft.id;
            contentHtml = seedHtml;
            contentJson = seedJson;
          }
        }

        setState({
          sessionId,
          draftId,
          policyId,
          sessionName,
          initialHtml: contentHtml,
          initialJson: contentJson,
          originalFileUrl,
          isLoading: false,
          isPending: false,
          error: null,
        });
      } catch (err: any) {
        console.error('Composer session init error:', err);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err.message || 'Failed to initialize session',
        }));
      }
    }

    init();
  }, [profile, urlPolicyId, isNew, mode, idempotencyKey]);

  // Private to the hook closure. Do NOT return from the hook — exporting it
  // would let callers bypass the isPending state machine and create rows
  // imperatively, violating the Section 10 invariant.
  const initializeSession = useCallback(async (): Promise<{
    sessionId: string;
    draftId: string;
    policyId: string;
  } | null> => {
    if (!stateRef.current.isPending) return null;
    if (initializingRef.current) return initializingRef.current;
    if (!profile) return null;

    const promise = (async () => {
      try {
        const currentName = stateRef.current.sessionName;

        const { data: newPolicyId, error: npErr } = await supabase
          .rpc('create_composer_draft_policy', {
            p_name: currentName,
            ...(idempotencyKey ? { p_idempotency_key: idempotencyKey } : {}),
          });
        if (npErr) throw npErr;
        if (!newPolicyId) throw new Error('Failed to create draft policy');

        // Client-layer idempotency for sessions/drafts: the RPC's
        // idempotency key only covers the policies row. Two-tab and
        // partial-failure retry scenarios would otherwise create duplicate
        // composer_sessions / policy_drafts rows. Reuse any existing row
        // owned by this user for this policy before inserting.
        let sessionId: string;
        let draftId: string;

        const { data: existingSession } = await supabase
          .from('composer_sessions')
          .select('id')
          .eq('policy_id', newPolicyId)
          .eq('created_by', profile.id)
          .eq('clinic_id', profile.clinic_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (existingSession) {
          sessionId = existingSession.id;
        } else {
          const { data: newSession, error: nsErr } = await supabase
            .from('composer_sessions')
            .insert({
              clinic_id: profile.clinic_id,
              created_by: profile.id,
              session_name: currentName,
              policy_id: newPolicyId,
            })
            .select('id')
            .single();
          if (nsErr) throw nsErr;
          sessionId = newSession.id;
        }

        const { data: existingDraft } = await supabase
          .from('policy_drafts')
          .select('id')
          .eq('session_id', sessionId)
          .eq('status', 'draft')
          .limit(1)
          .maybeSingle();

        if (existingDraft) {
          draftId = existingDraft.id;
        } else {
          const { data: newDraft, error: ndErr } = await supabase
            .from('policy_drafts')
            .insert({
              clinic_id: profile.clinic_id,
              session_id: sessionId,
              created_by: profile.id,
              content_html: '',
              content_json: null,
              status: 'draft',
              generation_mode: 'new_draft',
              policy_id: newPolicyId,
            })
            .select('id')
            .single();
          if (ndErr) throw ndErr;
          draftId = newDraft.id;
        }

        setState(prev => ({
          ...prev,
          sessionId,
          draftId,
          policyId: newPolicyId,
          sessionName: currentName,
          isPending: false,
          error: null,
        }));

        // Prime the effect's guard ref so the URL-swap-triggered re-run
        // (Branch 1 with urlPolicyId=newPolicyId) short-circuits instead of
        // re-querying and flashing isLoading=true — which would unmount
        // ComposerLayout and destroy the EditorPane (same destructive-unmount
        // problem state.error had; applies here to the re-entry case).
        lastInitializedPolicyId.current = newPolicyId;

        return { sessionId, draftId, policyId: newPolicyId };
      } catch (err: any) {
        console.error('initializeSession error:', err);
        // DO NOT set state.error here — would unmount the editor (via
        // ComposerWorkspace's error-screen branch) and destroy in-progress
        // TipTap content. state.error is reserved for mount-time init
        // failures (before the editor mounts). Lazy-init failures surface
        // via saveDraft's return value so the caller can toast + keep the
        // editor mounted for retry. isPending stays true; the next save
        // auto-retries via a fresh initializeSession call (initializingRef
        // is cleared by the promise.finally above).
        return null;
      }
    })();

    initializingRef.current = promise;
    // Clear the ref on settle (success or failure), but only if it still
    // points to this exact promise — prevents clobbering a later retry's
    // in-flight promise. A second caller during the same tick coalesces
    // onto this promise; a retry after failure gets a fresh attempt.
    promise.finally(() => {
      if (initializingRef.current === promise) {
        initializingRef.current = null;
      }
    });
    return promise;
  }, [profile, idempotencyKey]);

  const updateSessionName = useCallback(
    async (name: string) => {
      setState((prev) => ({ ...prev, sessionName: name }));
      // Pending: skip DB write. initializeSession() will use the latest
      // sessionName (via stateRef) when it creates the policy and session.
      if (!state.sessionId) return;
      await supabase
        .from('composer_sessions')
        .update({ session_name: name })
        .eq('id', state.sessionId);

      if (state.policyId) {
        await supabase
          .from('policies')
          .update({ name })
          .eq('id', state.policyId);
      }
    },
    [state.sessionId, state.policyId]
  );

  const saveDraft = useCallback(
    async (html: string, json: Json | null) => {
      let draftId = state.draftId;

      if (state.isPending) {
        const result = await initializeSession();
        if (!result) {
          toast.error('Save failed — please try again.');
          return false;
        }
        draftId = result.draftId;
      }

      if (!draftId) {
        console.warn('saveDraft: no draftId after initialize — aborting');
        return false;
      }

      const { error } = await supabase
        .from('policy_drafts')
        .update({ content_html: html, content_json: json })
        .eq('id', draftId);
      if (error) {
        console.error('Save draft error:', error);
        toast.error('Save failed — please try again.');
        return false;
      }
      return true;
    },
    [state.isPending, state.draftId, initializeSession]
  );

  return {
    ...state,
    updateSessionName,
    saveDraft,
  };
}
